import React, { useState, useEffect, useRef } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonItem,
  IonInput,
  IonButton,
  IonIcon,
  IonCard,
  IonCardContent,
  IonText,
  IonSpinner,
  IonToast,
  IonAlert,
} from '@ionic/react';
import {
  sendOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  personOutline,
  chatbubbleEllipsesOutline,
  sparklesOutline,
  bagOutline,
  calendarOutline,
  attachOutline,
  documentOutline,
} from 'ionicons/icons';
import { useAuth } from '../contexts/AuthContext';
import { useHistory } from 'react-router-dom';
import api from '../services/api';
import './Chat.css';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  metadata?: {
    intent?: string;
    confidence?: number;
    pendingActionId?: string;
    eventId?: string;
    showViewEventButton?: boolean;
  };
}

interface PendingAction {
  id: string;
  type: 'create_sale' | 'create_ticket';
  data: any;
  status: string;
}

const Chat: React.FC = () => {
  const { user } = useAuth();
  const history = useHistory();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  const [confirmingAction, setConfirmingAction] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    initializeChat();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = async () => {
    try {
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);

      // Create chat session
      await api.post(
        `/chat/sessions`,
        {},
        {
          params: { sessionId: newSessionId },
        }
      );

      // Add welcome message
      const welcomeMessage: ChatMessage = {
        id: 'welcome',
        role: 'assistant',
        content:
          'Hello! I can help you create sales or add tickets to your inventory. Just tell me what you\'d like to do. For example:\n\n• "I want to sell 2 tickets for Chelsea vs Arsenal"\n• "Add 4 tickets to inventory for Tottenham match"\n• "Create a sale for tomorrow\'s game"',
        timestamp: new Date().toISOString(),
      };

      setMessages([welcomeMessage]);
    } catch (error) {
      console.error('Error initializing chat:', error);
      setToastMessage('Error initializing chat');
      setShowToast(true);
    }
  };

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/png', 
      'image/jpg',
      'image/webp',
      'application/pdf'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      setToastMessage('Please upload an image (JPG, PNG, WEBP) or PDF file');
      setShowToast(true);
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setToastMessage('File size must be less than 10MB');
      setShowToast(true);
      return;
    }

    setUploadedFile(file);
  };

  const processFileWithAI = async (file: File) => {
    setIsProcessingFile(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/chat/sessions/${sessionId}/analyze-receipt`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Add user message showing file upload
      const userMessage: ChatMessage = {
        id: `user_${Date.now()}`,
        role: 'user',
        content: `📁 Uploaded ${file.name} for ticket extraction`,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Add AI response
      const assistantMessage: ChatMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        metadata: {
          intent: response.data.intent,
          confidence: response.data.confidence,
          pendingActionId: response.data.pendingActionId,
        },
      };
      setMessages((prev) => [...prev, assistantMessage]);

      // Handle pending actions if any
      if (response.data.pendingActionId) {
        setPendingActions((prev) => [
          ...prev,
          {
            id: response.data.pendingActionId,
            type: response.data.intent,
            data: response.data.extractedData,
            status: 'pending',
          },
        ]);
      }

    } catch (error) {
      console.error('Error processing file:', error);
      setToastMessage('Error processing file. Please try again.');
      setShowToast(true);
    } finally {
      setIsProcessingFile(false);
      setUploadedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || loading) return;

    const userMessage: ChatMessage = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: newMessage,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setNewMessage('');
    setLoading(true);

    try {
      const response = await api.post(`/chat/sessions/${sessionId}/messages`, {
        message: newMessage,
      });

      const assistantMessage: ChatMessage = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString(),
        metadata: {
          intent: response.data.intent,
          confidence: response.data.confidence,
          pendingActionId: response.data.pendingActionId,
        },
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // If there's a pending action, show confirmation buttons
      if (response.data.pendingActionId) {
        setPendingActions((prev) => [
          ...prev,
          {
            id: response.data.pendingActionId,
            type: response.data.intent,
            data: response.data.extractedData,
            status: 'pending',
          },
        ]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setToastMessage('Error sending message');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAction = (actionId: string) => {
    // Add haptic feedback for mobile devices
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
    setConfirmingAction(actionId);
    setShowConfirmAlert(true);
  };

  const confirmAction = async () => {
    if (!confirmingAction) return;

    try {
      const response = await api.post(
        `/chat/sessions/${sessionId}/actions/${confirmingAction}/confirm`
      );

      if (response.data.success) {
        // Remove from pending actions
        setPendingActions((prev) =>
          prev.filter((a) => a.id !== confirmingAction)
        );

        // Add success message
        const successMessage: ChatMessage = {
          id: `success_${Date.now()}`,
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date().toISOString(),
          metadata: {
            eventId: response.data.eventId,
            showViewEventButton: !!response.data.eventId,
          },
        };

        setMessages((prev) => [...prev, successMessage]);
        setToastMessage('Action completed successfully!');
      } else {
        setToastMessage(response.data.message || 'Error confirming action');
      }

      setShowToast(true);
    } catch (error) {
      console.error('Error confirming action:', error);
      setToastMessage('Error confirming action');
      setShowToast(true);
    } finally {
      setConfirmingAction(null);
    }
  };

  const cancelAction = async (actionId: string) => {
    // Add haptic feedback for mobile devices
    if ('vibrate' in navigator) {
      navigator.vibrate([30, 50, 30]);
    }
    try {
      const response = await api.post(
        `/chat/sessions/${sessionId}/actions/${actionId}/cancel`
      );

      if (response.data.success) {
        // Remove from pending actions
        setPendingActions((prev) => prev.filter((a) => a.id !== actionId));

        // Add cancellation message
        const cancelMessage: ChatMessage = {
          id: `cancel_${Date.now()}`,
          role: 'assistant',
          content: response.data.message,
          timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, cancelMessage]);
      }
    } catch (error) {
      console.error('Error cancelling action:', error);
      setToastMessage('Error cancelling action');
      setShowToast(true);
    }
  };

  const handleKeyPress = (e: any) => {
    // Handle both Ionic's keyboard event and standard keyboard event
    const key = e.detail?.keyboardKey || e.key;
    const shiftKey = e.detail?.shiftKey || e.shiftKey;
    
    if (key === 'Enter' && !shiftKey && !loading && newMessage.trim()) {
      e.preventDefault();
      e.stopPropagation();
      sendMessage();
    }
  };

  const navigateToEvent = (eventId: string) => {
    history.push(`/events/${eventId}`);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>AI Assistant</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="chat-content">
        <div className="chat-messages">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.role}`}>
              <div className="message-avatar">
                <IonIcon
                  icon={
                    message.role === 'user'
                      ? personOutline
                      : chatbubbleEllipsesOutline
                  }
                />
              </div>
              <div className="message-bubble">
                <div className="message-content">
                  {message.content.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index < message.content.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
                {message.metadata?.showViewEventButton && message.metadata?.eventId && (
                  <div className="message-action">
                    <IonButton
                      size="small"
                      fill="outline"
                      color="primary"
                      onClick={() => navigateToEvent(message.metadata!.eventId!)}
                    >
                      <IonIcon icon={calendarOutline} slot="start" />
                      View Event
                    </IonButton>
                  </div>
                )}
                <div className="message-time">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {/* Pending Actions */}
          {pendingActions.map((action) => (
            <div key={action.id} className="pending-action-wrapper">
              <div className="confirmation-card">
                <div className="confirmation-header">
                  <div className="confirmation-icon">
                    <IonIcon icon={action.type === 'create_sale' ? sparklesOutline : bagOutline} />
                  </div>
                  <div className="confirmation-title">
                    <h3>{action.type === 'create_sale' ? 'Sale Confirmation' : 'Inventory Addition'}</h3>
                    <p>Please review the details and confirm to proceed</p>
                  </div>
                </div>
                
                <div className="confirmation-actions">
                  <button
                    className="confirm-btn confirm-yes"
                    onClick={() => handleConfirmAction(action.id)}
                  >
                    <div className="btn-content">
                      <div className="btn-icon">
                        <IonIcon icon={checkmarkCircleOutline} />
                      </div>
                      <div className="btn-text">
                        <span className="btn-main">Confirm</span>
                        <span className="btn-sub">{action.type === 'create_sale' ? 'Create Sale' : 'Add to Inventory'}</span>
                      </div>
                    </div>
                    <div className="btn-ripple"></div>
                  </button>
                  
                  <button
                    className="confirm-btn confirm-no"
                    onClick={() => cancelAction(action.id)}
                  >
                    <div className="btn-content">
                      <div className="btn-icon">
                        <IonIcon icon={closeCircleOutline} />
                      </div>
                      <div className="btn-text">
                        <span className="btn-main">Cancel</span>
                        <span className="btn-sub">Discard</span>
                      </div>
                    </div>
                    <div className="btn-ripple"></div>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="message assistant">
              <div className="message-avatar">
                <IonIcon icon={chatbubbleEllipsesOutline} />
              </div>
              <div className="message-bubble">
                <IonSpinner name="dots" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          {uploadedFile && (
            <div className="file-preview">
              <IonCard>
                <IonCardContent>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IonIcon 
                        icon={uploadedFile.type.startsWith('image/') ? attachOutline : documentOutline} 
                        color="primary" 
                      />
                      <span style={{ fontSize: '14px' }}>{uploadedFile.name}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <IonButton 
                        size="small" 
                        fill="solid" 
                        color="primary"
                        onClick={() => processFileWithAI(uploadedFile)}
                        disabled={isProcessingFile}
                      >
                        {isProcessingFile ? <IonSpinner name="dots" /> : 'Extract Tickets'}
                      </IonButton>
                      <IonButton 
                        size="small" 
                        fill="clear" 
                        color="medium"
                        onClick={() => setUploadedFile(null)}
                        disabled={isProcessingFile}
                      >
                        Cancel
                      </IonButton>
                    </div>
                  </div>
                </IonCardContent>
              </IonCard>
            </div>
          )}
          
          <IonItem className="chat-input-item">
            <IonButton
              fill="clear"
              slot="start"
              onClick={triggerFileUpload}
              disabled={loading || isProcessingFile}
              title="Upload receipt or ticket image"
            >
              <IonIcon icon={attachOutline} />
            </IonButton>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            
            <IonInput
              value={newMessage}
              placeholder="Type your message or upload a receipt/ticket image..."
              onIonInput={(e) => setNewMessage(e.detail.value!)}
              onKeyPress={handleKeyPress}
              onKeyDown={handleKeyPress}
              disabled={loading || isProcessingFile}
            />
            <IonButton
              fill="clear"
              slot="end"
              onClick={sendMessage}
              disabled={!newMessage.trim() || loading || isProcessingFile}
            >
              <IonIcon icon={sendOutline} />
            </IonButton>
          </IonItem>
        </div>
      </IonContent>

      <IonAlert
        isOpen={showConfirmAlert}
        onDidDismiss={() => setShowConfirmAlert(false)}
        header="Confirm Action"
        message="Are you sure you want to proceed with this action?"
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            handler: () => setConfirmingAction(null),
          },
          {
            text: 'Confirm',
            handler: confirmAction,
          },
        ]}
      />

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="top"
      />
    </IonPage>
  );
};

export default Chat;
